import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGenericConversationMessages } from "../hooks/useGenericConversationMessages";
import { MessageWithId } from "../store/conversationMessagesSlice";
import { AgentContext, GenericAgentConversation } from "../types/agentConfig";
import { LoadingStatus } from "./LoadingStatus";
import {
  AgentMessageBubble,
  ErrorMessage,
  UserMessage,
} from "./messageTypes";
import { UnifiedToolComponent, ToolState } from "./toolResults";

interface GenericChatMessagesProps {
  context: AgentContext;
  conversation: GenericAgentConversation;
  isLocallyLoading?: boolean;
}

export const GenericChatMessages: React.FC<GenericChatMessagesProps> = ({
  context,
  conversation,
  isLocallyLoading = false,
}) => {
  const conversationId = conversation.id;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);

  const { messages, loadingStatus, error } = useGenericConversationMessages(
    context,
    conversationId
  );

  // Helper function to detect if any message is currently streaming
  const isAnyMessageStreaming = useMemo(() => {
    return messages.some((message) => {
      if (message.type === "agent") {
        const hasTokens = message.content.tokens && message.content.tokens.length > 0;
        const isComplete = !!message.content.text;
        return hasTokens && !isComplete;
      }
      return false;
    });
  }, [messages]);

  // Detect if we have new messages (vs just token updates to existing messages)
  const hasNewMessages = useMemo(() => {
    return messages.length > previousMessageCount;
  }, [messages.length, previousMessageCount]);

  useEffect(() => {
    // Update message count tracking
    if (messages.length !== previousMessageCount) {
      setPreviousMessageCount(messages.length);
    }

    // For initial load, scroll to center
    if (!hasInitiallyScrolled && messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "instant",
        block: "center",
      });
      setHasInitiallyScrolled(true);
    } 
    // For new messages: scroll to the latest message
    else if (hasInitiallyScrolled && hasNewMessages && messages.length > 0) {
      // Find the last message element and scroll to it
      const lastMessageIndex = messages.length - 1;
      const lastMessageElement = containerRef.current?.querySelector(`[data-message-index="${lastMessageIndex}"]`);
      
      if (lastMessageElement) {
        lastMessageElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
    // For loading/working state changes without new messages: scroll to end if not streaming
    else if (hasInitiallyScrolled && !hasNewMessages && !isAnyMessageStreaming && (conversation.metadata.isLoading || conversation.metadata.isWorking) && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages, conversation.metadata.isLoading, conversation.metadata.isWorking, hasInitiallyScrolled, isAnyMessageStreaming, hasNewMessages, previousMessageCount]);

  // Reset scroll state when conversation changes
  useEffect(() => {
    setHasInitiallyScrolled(false);
    setPreviousMessageCount(0);
  }, [conversationId]);

  if (loadingStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Create a helper function to determine tool state
  const getToolState = (toolCallMessage: Extract<MessageWithId, { type: "tool_call" }>, index: number): ToolState => {
    // Check if there's a corresponding tool result message after this tool call
    const hasResult = messages
      .slice(index + 1)
      .some((msg) => 
        msg.type === "tool_result" && 
        msg.content.toolName === toolCallMessage.content.toolName
      );
    
    if (hasResult) {
      return "completed";
    }
    
    // If conversation is loading/working, show executing state
    if (conversation?.metadata.isLoading || conversation?.metadata.isWorking) {
      return "executing";
    }
    
    // Default to loading state
    return "loading";
  };

  const renderMessage = (message: MessageWithId, index: number) => {
    switch (message.type) {
      case "user":
        return (
          <div key={message.id} data-message-index={index}>
            <UserMessage message={message} />
          </div>
        );
      case "agent":
        return (
          <div key={message.id} data-message-index={index}>
            <AgentMessageBubble
              message={message}
              conversationId={conversationId}
            />
          </div>
        );
      case "tool_call":
        // Find the corresponding tool result message if it exists
        const toolResultMessage = messages
          .slice(index + 1)
          .find((msg) => 
            msg.type === "tool_result" && 
            msg.content.toolName === message.content.toolName
          ) as Extract<MessageWithId, { type: "tool_result" }> | undefined;
        
        const toolState = getToolState(message, index);
        
        return (
          <div key={message.id} data-message-index={index}>
            <UnifiedToolComponent
              toolCallMessage={message}
              toolResultMessage={toolResultMessage}
              state={toolState}
            />
          </div>
        );
      case "tool_result":
        // Tool results are now handled by tool_call rendering
        // Skip rendering tool_result messages directly
        return null;
      case "system_initiation":
        // System initiation messages are hidden from the UI
        return null;
      case "error":
        return (
          <div key={message.id} data-message-index={index}>
            <ErrorMessage message={message} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflow: "auto",
        p: 2,
        pb: 4,
      }}
    >
      {messages.length === 0 ? (
        // Show loading if we're loading messages OR if locally loading
        (loadingStatus as string) === "loading" || isLocallyLoading ? (
          <LoadingStatus
            isLoading={true}
            isWorking={false}
          />
        ) : (
          <Typography color="text.secondary" align="center">
            What would you like to know?
          </Typography>
        )
      ) : (
        <>
          {messages.map((message, index) => renderMessage(message, index))}
          <LoadingStatus
            isLoading={(conversation?.metadata.isLoading ?? false) || isLocallyLoading}
            isWorking={conversation?.metadata.isWorking ?? false}
          />
        </>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};